// Thin fetch wrapper that talks to the Flask backend's /r/admin/* routes.
//
// Design notes:
// - No third-party HTTP client. The whole admin SPA is internal-only and
//   the API surface is JSON-in / JSON-out, so the native fetch is plenty.
// - The session id is held on the client instance, not threaded through
//   every call site. AuthProvider drives setSession/clearSession.
// - 401 responses fan out to a single onUnauthorized listener (set by
//   AuthProvider) so a stale session triggers re-login from any page
//   without each query needing its own retry plumbing.
// - Errors are typed (AdminApiUnauthorized / AdminApiForbidden /
//   AdminApiError / AdminApiNetworkError) so React Query's `error`
//   branches can render category-appropriate messaging.
// - The backend returns three envelope shapes:
//     1. ``{ success: true, data: T }`` (no pagination)
//     2. ``{ success: true, data: T, next_cursor: string }`` (paginated)
//     3. ``{ success: false, error: string }``
//   ``send()`` returns the entire parsed body verbatim and only throws
//   on shape #3 (or HTTP failures), so callers asking for paginated
//   endpoints still receive ``next_cursor``. Earlier revisions of this
//   client auto-unwrapped ``data`` and silently dropped sibling
//   metadata; addressing that gap is the whole point of typing T as
//   the *full* envelope shape and handing it back unchanged.

export type FetchImpl = typeof fetch

export interface AdminApiClientOptions {
  baseUrl: string
  fetchImpl?: FetchImpl
}

export class AdminApiError extends Error {
  readonly status: number
  readonly errorCode: string

  constructor(message: string, status: number, errorCode: string) {
    super(message)
    this.name = 'AdminApiError'
    this.status = status
    this.errorCode = errorCode
  }
}

export class AdminApiUnauthorized extends AdminApiError {
  constructor(errorCode: string) {
    super(`Unauthorized: ${errorCode || 'UNKNOWN'}`, 401, errorCode)
    this.name = 'AdminApiUnauthorized'
  }
}

export class AdminApiForbidden extends AdminApiError {
  constructor(errorCode: string) {
    super(`Forbidden: ${errorCode || 'UNKNOWN'}`, 403, errorCode)
    this.name = 'AdminApiForbidden'
  }
}

export class AdminApiNetworkError extends Error {
  constructor(public readonly cause: unknown) {
    super(
      cause instanceof Error
        ? `Network error: ${cause.message}`
        : 'Network error',
    )
    this.name = 'AdminApiNetworkError'
  }
}

type UnauthorizedCallback = (e: AdminApiUnauthorized) => void

export class AdminApiClient {
  private readonly baseUrl: string
  private readonly fetchImpl: FetchImpl
  private sessionId: string | null = null
  private unauthorizedCb: UnauthorizedCallback | null = null

  constructor(opts: AdminApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '')
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis)
  }

  setSession(sessionId: string): void {
    this.sessionId = sessionId
  }

  clearSession(): void {
    this.sessionId = null
  }

  onUnauthorized(cb: UnauthorizedCallback | null): void {
    this.unauthorizedCb = cb
  }

  get<T>(path: string): Promise<T> {
    return this.send<T>('GET', path)
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>('POST', path, body)
  }

  private async send<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}/${path.replace(/^\/+/, '')}`
    const headers = new Headers()
    if (body !== undefined) headers.set('Content-Type', 'application/json')
    if (this.sessionId) headers.set('X-Operator-Session', this.sessionId)
    const init: RequestInit = { method, headers }
    if (body !== undefined) init.body = JSON.stringify(body)

    let response: Response
    try {
      response = await this.fetchImpl(url, init)
    } catch (e) {
      throw new AdminApiNetworkError(e)
    }

    const parsed = await this.parseBody(response)

    if (response.status === 401) {
      const errCode = extractErrorCode(parsed)
      const e = new AdminApiUnauthorized(errCode)
      // Fire on every 401 so AuthProvider can re-evaluate; any rate
      // limiting is the listener's responsibility.
      if (this.unauthorizedCb) this.unauthorizedCb(e)
      throw e
    }
    if (response.status === 403) {
      throw new AdminApiForbidden(extractErrorCode(parsed))
    }
    if (response.status >= 400) {
      throw new AdminApiError(
        `HTTP ${response.status}`,
        response.status,
        extractErrorCode(parsed),
      )
    }
    if (
      parsed && typeof parsed === 'object' && 'success' in parsed &&
      (parsed as { success?: unknown }).success === false
    ) {
      throw new AdminApiError(
        'API returned success:false',
        response.status,
        extractErrorCode(parsed),
      )
    }
    // Return the entire parsed body. Callers ask for the envelope shape
    // they actually need (e.g. `{ success: true; data: T; next_cursor:
    // string }`) so paginated metadata round-trips verbatim. Empty
    // bodies (DELETE-style 204s) come back as `null`, which TypeScript
    // narrows via the caller's chosen T.
    return parsed as T
  }

  private async parseBody(response: Response): Promise<unknown> {
    const text = await response.text()
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
}

function extractErrorCode(parsed: unknown): string {
  if (parsed && typeof parsed === 'object' && 'error' in parsed) {
    const err = (parsed as { error?: unknown }).error
    if (typeof err === 'string') return err
  }
  return ''
}
